import '@expo/metro-runtime';
import './src/common/Extensions';
import React, { useCallback, useEffect, useState } from 'react';
import { useWindowDimensions, ActivityIndicator, Alert, AppState, Image, LogBox, Platform } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';
import { setJSExceptionHandler, setNativeExceptionHandler } from 'react-native-exception-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { ThemeProvider } from '@rneui/themed';
import * as ExpoApplication from 'expo-application';
import { useAssets } from 'expo-asset';
import Constants from 'expo-constants';
import * as ExpoDevice from 'expo-device';
import * as ExpoFileLogger from 'expo-file-logger';
import * as Fonts from 'expo-font';
import * as ScreenOrientation from 'expo-screen-orientation';
import * as SplashScreen from 'expo-splash-screen';
// import * as Sentry  from 'sentry-expo';
import { fetchStartupData } from './src/common/Api';
import { parseNavigation } from './src/common/helpers/Navigation';
import { AppContextProvider } from './src/contexts/App';
import { AuthContextProvider } from './src/contexts/Auth';
import { LayoutContextProvider } from './src/contexts/Layout';
import { MainView } from './src/views/Main';
import { generateTheme } from './src/themes';

// Prevent logging on dev for issues that are library dependent and not actually errors
if (__DEV__) {
  const ignoreErrors = [
                        'Support for defaultProps will be removed', 
                        'findDOMNode is deprecated and will be removed in the next major release',
                        'validateDOMNesting(...): <button> cannot appear as a descendant of <button>.', // this needs be addressed as part of removal of @rneui dependencies
                    ];

  const e = console.error;
  console.error = (...arg) => {
    for (const error of ignoreErrors) if (arg[0].includes(error)) return;
    e(...arg);
  };

  LogBox.ignoreLogs(ignoreErrors);
}
// End

if (!Platform.OS === 'web') {
    setJSExceptionHandler((e, isFatal) => {
        ExpoFileLogger.log(isFatal ? 'fatal' : 'error', `Encountered JS exception: ${e.message}\n${e}`);

        if (isFatal) {
            if (!Platform.OS.equals('web')) {
                Alert.alert(
                    'Oops, an error occured',
                    `
                        Error: ${e.name} ${e.message}
                        We have reported this to our team. Please close and restart the app.
                    `,
                    [{ text: 'Close' }]
                );
            }
        }
    });

    setNativeExceptionHandler(e => ExpoFileLogger.log('fatal', `Encountered Native exception: ${e.message}\n${e}`));
}

// if (Constants.manifest2?.extra?.sentryDsn) {
//     Sentry.init({
//         dsn: Constants.manifest?.extra?.sentryDsn,
//         debug: true,
//         enableAppHangTracking: true,
//         enableInExpoDevelopment: false,
//         enableOutOfMemoryTracking: true,
//         tracesSampleRate: 1.0,
//     });
// }

SplashScreen.preventAutoHideAsync();

const App = () => {
    const [globals, setGlobals] = useState();
    const { height, width } = useWindowDimensions();
    const isNative = Platform.OS.equals('ios') || Platform.OS.equals('android');
    const startPage = isNative ? '/app' : window.location.pathname || '/';
    const [assets, error] = useAssets(require('./assets/images/splash.png'));

    const loadFonts = useCallback(async () => {
        await Fonts.loadAsync({
            BarlowMedium: require('./assets/fonts/BarlowCondensed-Medium.ttf'),
            BarlowRegular: require('./assets/fonts/BarlowCondensed-Regular.ttf'),
            FrankRuhlLibre: require('./assets/fonts/FrankRuhlLibre.ttf'),
            GothamBook: require('./assets/fonts/GothamBook.ttf'),
            GothamMedium: require('./assets/fonts/GothamMedium.ttf'),
            Plantin: require('./assets/fonts/Plantin.ttf')
        });
    }, []);

    useEffect(() => {
        let appStateChangeSubscription;
        let appStateMemoryWarnSubscription;

        const initialize = async () => {
            ExpoFileLogger.log(
                'debug',
                `\n
          Device Info
 *******************************
 OS Version:        ${ExpoDevice.osVersion}
 OS Name:           ${ExpoDevice.osName}
 Brand:             ${ExpoDevice.brand}
 Model:             ${ExpoDevice.modelName}
 Model Year:        ${ExpoDevice.deviceYearClass}
 Total Memory:      ${ExpoDevice.totalMemory}
 App Version:       ${ExpoApplication.nativeApplicationVersion}
 App Build Version: ${ExpoApplication.nativeBuildVersion}
********************************
 `
            );

            ExpoFileLogger.log('debug', 'Initializing application');

            await loadFonts();

            ExpoFileLogger.log('debug', 'Fonts loaded successfully!');

            const tmpGlobals = await fetchStartupData(startPage);

            ExpoFileLogger.log('debug', 'Startup data fetched, Parsing navigation ...');

            if (tmpGlobals) {
                tmpGlobals.navigation = parseNavigation(tmpGlobals.navigation, tmpGlobals.settings.websiteSettings);
                setGlobals(tmpGlobals);
            }

            if (!Platform.OS.equals('web')) {
                ScreenOrientation.addOrientationChangeListener(e => {
                    ExpoFileLogger.log('debug', `ScreenOrientation changed to: ${e.orientationInfo.orientation}`);
                });

                appStateChangeSubscription = AppState.addEventListener('change', state => {
                    ExpoFileLogger.log('debug', `AppState changed [${state}]`);
                });

                appStateMemoryWarnSubscription = AppState.addEventListener('memoryWarning', e => {
                    ExpoFileLogger.log('warn', `AppState memoryWarning encountered: ${e}`);
                });
            }

            ExpoFileLogger.log('debug', 'Initialization complete!');
        };

        initialize();

        return () => {
            appStateChangeSubscription?.remove();
            appStateMemoryWarnSubscription?.remove();
            ScreenOrientation?.removeOrientationChangeListeners();
        };
    }, [loadFonts, startPage]);

    if (!globals) {
        if (Platform.OS.equals('web')) {
            if (assets && !error) {
                return (
                    <Animated.View
                        entering={FadeIn}
                        exiting={FadeOut}
                        style={{
                            alignItems: 'center',
                            backgroundColor: Constants.expoConfig.extra?.splashScreenColor,
                            height: height,
                            justifyContent: 'center',
                            width: width
                        }}
                    >
                        <Image
                            source={assets[0]}
                            style={{
                                color: '#fff',
                                height: 200,
                                marginBottom: 10,
                                width: 400
                            }}
                        />
                        <ActivityIndicator color='#fff' size='large' />
                    </Animated.View>
                );
            }
        }

        return null;
    }

    return (
        <ThemeProvider theme={generateTheme(Constants.expoConfig.extra?.appTheme)}>
            <AppContextProvider globals={globals}>
                <LayoutContextProvider siteWrapper={globals?.siteWrapper}>
                    <AuthContextProvider>
                        <SafeAreaProvider>
                            <MainView />
                        </SafeAreaProvider>
                    </AuthContextProvider>
                </LayoutContextProvider>
            </AppContextProvider>
        </ThemeProvider>
    );
};

export default App;
